import apiClient, { unwrap } from './client';

// Batch: backend `batchCode` → frontend `name`, handle nested incubator object
function batchToFrontend(batch) {
  const incubatorId = batch.incubatorId ?? batch.incubator?.id ?? null;
  return { ...batch, name: batch.batchCode ?? batch.name, incubatorId };
}

function batchToRequest(batch) {
  const { name, transfers, chickLosses, incubatorName, incubator, sourceRoomName, sourceFlockName, ...rest } = batch;
  return { ...rest, batchCode: name };
}

// BatchTransfer: backend `transferDate` → frontend `date`, normalize batchId field
function transferToFrontend(t) {
  return {
    ...t,
    date: t.transferDate ?? t.date,
    batchId: t.batchId ?? t.incubatorBatchId ?? t.incubator_batch_id ?? t.batch_id ?? null,
    // Include flock details from backend (indicates if new flock was created)
    flock: t.flock ?? null,
  };
}

function transferToRequest(t) {
  const { date, flock, ...rest } = t;
  // flockId is now OPTIONAL - if not provided, backend auto-creates/finds flock
  // This prevents mixed-age flocks by ensuring one-flock-per-batch
  return {
    ...rest,
    transferDate: date,
    flockId: t.flockId ?? null,  // null = let backend auto-resolve
  };
}

// ChickLoss: normalize batchId field from backend variations
function chickLossToFrontend(l) {
  return {
    ...l,
    batchId: l.batchId ?? l.incubatorBatchId ?? l.incubator_batch_id ?? l.batch_id ?? null,
  };
}

export const incubatorApi = {
  // ── Incubator Machines ────────────────────────────────────────────────────
  getAllMachines: async () => {
    const res = await apiClient.get('/incubator/machines');
    return unwrap(res) ?? [];
  },

  createMachine: async (machine) => {
    const res = await apiClient.post('/incubator/machines', machine);
    return unwrap(res);
  },

  updateMachine: async (id, machine) => {
    const res = await apiClient.put(`/incubator/machines/${id}`, machine);
    return unwrap(res);
  },

  deleteMachine: async (id) => {
    await apiClient.delete(`/incubator/machines/${id}`);
  },

  // ── Batches ───────────────────────────────────────────────────────────────
  getAllBatches: async () => {
    const res = await apiClient.get('/incubator/batches', { params: { size: 200, sort: 'startDate,desc' } });
    const page = unwrap(res);
    return (page?.content ?? page ?? []).map(batchToFrontend);
  },

  createBatch: async (batch) => {
    const res = await apiClient.post('/incubator/batches', batchToRequest(batch));
    return batchToFrontend(unwrap(res));
  },

  updateBatch: async (id, batch) => {
    const res = await apiClient.put(`/incubator/batches/${id}`, batchToRequest(batch));
    return batchToFrontend(unwrap(res));
  },

  deleteBatch: async (id) => {
    await apiClient.delete(`/incubator/batches/${id}`);
  },

  // ── Transfers ─────────────────────────────────────────────────────────────
  getAllTransfers: async () => {
    const res = await apiClient.get('/incubator/transfers', { params: { size: 500 } });
    const page = unwrap(res);
    return (page?.content ?? page ?? []).map(transferToFrontend);
  },

  createTransfer: async (transfer) => {
    const res = await apiClient.post('/incubator/transfers', transferToRequest(transfer));
    return transferToFrontend(unwrap(res));
  },

  deleteTransfer: async (id) => {
    await apiClient.delete(`/incubator/transfers/${id}`);
  },

  // ── Chick Losses ──────────────────────────────────────────────────────────
  getAllChickLosses: async () => {
    const res = await apiClient.get('/incubator/chick-losses', { params: { size: 500 } });
    const page = unwrap(res);
    return (page?.content ?? page ?? []).map(chickLossToFrontend);
  },

  createChickLoss: async (loss) => {
    const res = await apiClient.post('/incubator/chick-losses', loss);
    return chickLossToFrontend(unwrap(res));
  },

  deleteChickLoss: async (id) => {
    await apiClient.delete(`/incubator/chick-losses/${id}`);
  },
};

// Merge flat transfers + chickLosses arrays into their parent batch objects
export function mergeBatchChildren(batches, transfers, chickLosses) {
  return batches.map((batch) => ({
    ...batch,
    transfers:   transfers.filter((t) => t.batchId === batch.id),
    chickLosses: chickLosses.filter((l) => l.batchId === batch.id),
  }));
}
